import { useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { impersonateUser } from '../services/api';

export default function UserDetailModal({ 
  isOpen, 
  onClose, 
  userId, 
  fetchUser,
  onStatusToggle,
  onAdminToggle,
  onTierChange,
  onDelete,
}) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showEmail, setShowEmail] = useState(false);
  const modalContentRef = useRef(null);

  // `useAuth` throws when this component is rendered outside of an AuthProvider
  // (unit tests). Guard against that so tests can render the modal without a
  // provider by falling back to a null `currentUser`. Keep a reference to the
  // auth object when available so we can call `checkAuth()` after impersonation.
  let currentUser = null;
  let auth = null;
  try {
    auth = useAuth();
    currentUser = auth?.user || null;
  } catch (e) {
    currentUser = null;
    auth = null;
  }

  const [impersonating, setImpersonating] = useState(false);
  const [activeTab, setActiveTab] = useState('info');

  useEffect(() => {
    if (isOpen && userId) {
      loadUserDetails();
    }
    setShowEmail(false);
    if (isOpen) setActiveTab('info');
  }, [isOpen, userId]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  const loadUserDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const userData = await fetchUser(userId);
      setUser(userData);
    } catch (err) {
      setError(err.message || 'Failed to load user details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActionLabel = (action) => {
    const labels = {
      status_change: 'Status Changed',
      admin_change: 'Admin Status Changed',
      tier_change: 'Subscription Tier Changed',
      hard_delete: 'Hard Deleted',
    };
    return labels[action] || action;
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div 
        ref={modalContentRef}
        className="bg-dark-800 border border-dark-700 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-dark-700 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">User Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
            aria-label="Close modal"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                <p className="mt-2 text-gray-400">Loading user details...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="p-4 bg-red-900/20 border border-red-500/30 rounded-lg text-red-400">
              <p className="font-medium">Error loading user details</p>
              <p className="text-sm mt-1">{error}</p>
            </div>
          )}

          {!loading && !error && user && (
            <div className="space-y-6">
              {/* Tabs */}
              <div className="border-b border-dark-700 mb-4">
                <nav className="flex space-x-2" role="tablist" aria-label="User detail tabs">
                  <button
                    role="tab"
                    aria-selected={activeTab === 'info'}
                    onClick={() => setActiveTab('info')}
                    className={`px-3 py-2 text-sm rounded-t-lg ${activeTab === 'info' ? 'bg-dark-700 text-white' : 'text-gray-400 hover:text-white'}`}
                  >
                    User Information
                  </button>
                  <button
                    role="tab"
                    aria-selected={activeTab === 'subscription'}
                    onClick={() => setActiveTab('subscription')}
                    className={`px-3 py-2 text-sm rounded-t-lg ${activeTab === 'subscription' ? 'bg-dark-700 text-white' : 'text-gray-400 hover:text-white'}`}
                  >
                    Subscription Details
                  </button>
                  <button
                    role="tab"
                    aria-selected={activeTab === 'history'}
                    onClick={() => setActiveTab('history')}
                    className={`px-3 py-2 text-sm rounded-t-lg ${activeTab === 'history' ? 'bg-dark-700 text-white' : 'text-gray-400 hover:text-white'}`}
                  >
                    Admin Actions
                  </button>
                </nav>
              </div>

              {/* Tab panels */}
              {activeTab === 'info' && (
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4">User Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-400 block mb-1">Username</label>
                      <div className="flex items-center gap-3">
                        <div className="shrink-0">
                          <img
                            src={user.profile_image_url || user.profileImageUrl || user.profile?.profile_image_url}
                            alt={user.display_name || user.displayName || user.twitch_username || 'User avatar'}
                            className="w-12 h-12 rounded-full object-cover bg-dark-700"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        </div>
                        <p className="text-white">{user.display_name || user.displayName || user.twitch_username}</p>
                        {user.is_admin && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium bg-purple-900/30 text-purple-400 border border-purple-500/30">
                            {user.isRootAdmin ? 'Root Admin' : 'Admin'}
                          </span>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-400 block mb-1">Email</label>
                      <div className="flex items-center gap-3">
                        {/* c8 ignore start */}
                        <p className="text-white truncate flex-1 min-w-0">
                          {showEmail ? (user.email || user.profile?.email || user.twitch_email || '-') : 'Hidden'}
                        </p>
                        <button
                          onClick={() => { if (!currentUser?.isRootAdmin) return; setShowEmail((s) => !s); }}
                          className={
                            `px-2 py-1 text-sm rounded-sm transition-colors shrink-0 ${currentUser?.isRootAdmin ? 'bg-dark-700 hover:bg-dark-600 text-white' : 'bg-dark-900 text-gray-500 cursor-not-allowed'}`
                          }
                          aria-pressed={showEmail}
                          aria-disabled={!currentUser?.isRootAdmin}
                          title={!currentUser?.isRootAdmin ? 'Restricted to root admin' : undefined}
                          disabled={!currentUser?.isRootAdmin}
                        >
                          {showEmail ? 'Hide' : 'Show email'}
                        </button>
                        {/* c8 ignore stop */}
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-400 block mb-1">User ID</label>
                      <p className="text-gray-300 font-mono text-sm">{user.id}</p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-400 block mb-1">Twitch ID</label>
                      <p className="text-gray-300 font-mono text-sm">{user.twitch_id}</p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-400 block mb-1">Status</label>
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            user.status === 'active'
                              ? 'bg-green-900/30 text-green-400 border border-green-500/30'
                              : (user.status === 'disabled' || user.status === 'suspended')
                              ? 'bg-yellow-900/30 text-yellow-400 border border-yellow-500/30'
                              : 'bg-red-900/30 text-red-400 border border-red-500/30'
                        }`}
                      >
                        {user.status}
                      </span>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-400 block mb-1">Subscription Tier</label>
                      <p className="text-white">{user.subscription_tier_name || 'None'}</p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-400 block mb-1">Joined</label>
                      <p className="text-gray-300">{formatDate(user.created_at)}</p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-400 block mb-1">Last Updated</label>
                      <p className="text-gray-300">{formatDate(user.updated_at)}</p>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'subscription' && (
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4">Subscription Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-400 block mb-1">Current Tier</label>
                      <p className="text-white">{user.subscription_tier_name || 'None'}</p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-400 block mb-1">Subscription Status</label>
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          user.subscription_status === 'active'
                            ? 'bg-green-900/30 text-green-400 border border-green-500/30'
                            : user.subscription_status === 'trialing'
                            ? 'bg-blue-900/30 text-blue-400 border border-blue-500/30'
                            : user.subscription_status === 'past_due'
                            ? 'bg-yellow-900/30 text-yellow-400 border border-yellow-500/30'
                            : 'bg-gray-900/30 text-gray-400 border border-gray-500/30'
                        }`}
                      >
                        {user.subscription_status || 'None'}
                      </span>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-400 block mb-1">Expiration Date</label>
                      <p className="text-gray-300">{user.subscription_expires_at ? formatDate(user.subscription_expires_at) : '-'}</p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-400 block mb-1">Next Billing Date</label>
                      <p className="text-gray-300">{user.subscription_started_at ? formatDate(user.subscription_started_at) : '-'}</p>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'history' && (
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4">Admin Actions</h3>
                  {user.admin_actions && user.admin_actions.length > 0 ? (
                    <div className="bg-dark-900 border border-dark-600 rounded-lg overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-dark-700 border-b border-dark-600">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                              Date
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                              Action
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                              Details
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                              Performed By
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-dark-700">
                          {user.admin_actions.map((action) => (
                            <tr key={action.id} className="hover:bg-dark-750 transition-colors">
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                                {formatDate(action.created_at)}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-white">
                                {getActionLabel(action.action || action.action_type)}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-300">
                                  {action.details
                                    ? (typeof action.details === 'string'
                                        ? action.details
                                        : JSON.stringify(action.details))
                                    : '-'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                                {action.admin_display_name || action.admin_username || 'System'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-dark-900 border border-dark-600 rounded-lg">
                      <p className="text-gray-400">No admin actions recorded for this user.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-dark-700 flex justify-between items-center gap-4">
          {/* Action buttons (only show when user data loaded) */}
          {!loading && !error && user && (
            <div className="flex gap-2 flex-wrap">
              {activeTab === 'info' && (
                <>
                  <a
                    href={`/admin/newsletter?username=${encodeURIComponent(user.display_name || user.twitch_username || user.email || 'user')}`}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium text-sm"
                    title="Send a message to this user"
                  >
                    Send Message
                  </a>

                  {currentUser?.isRootAdmin ? (
                    <>
                      <div className="flex gap-2">
                        {/* c8 ignore start */}
                        <button
                          onClick={async () => {
                            if (!user || !auth?.user) return;
                            const ok = window.confirm(`You will be logged out and logged in as ${user.display_name || user.twitch_username || user.twitch_username}. Continue?`);
                            if (!ok) return;
                            try {
                              setImpersonating(true);
                              const res = await impersonateUser(user.id);
                              try { localStorage.setItem('impersonatedBy', String(auth.user.id)); } catch (e) { }
                              try { if (res && res.token) localStorage.setItem('impersonation_token', res.token); } catch (e) {}
                              if (auth?.checkAuth) await auth.checkAuth();
                              window.location.href = '/dashboard';
                            } catch (err) {
                              console.error('Impersonation failed', err);
                              try { window.alert(err.message || 'Failed to impersonate user'); } catch (e) {}
                            } finally {
                              setImpersonating(false);
                            }
                          }}
                          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors font-medium text-sm disabled:opacity-50 cursor-pointer"
                          disabled={impersonating}
                        >
                          {impersonating ? 'Impersonating...' : 'Log in as User'}
                        </button>
                        {/* c8 ignore stop */}
                      </div>
                    </>
                  ) : (
                    <div className="text-sm text-gray-400">View only — management restricted to Root Admins</div>
                  )}
                </>
              )}

              {activeTab === 'subscription' && (
                <div className="flex gap-2">
                  {currentUser?.isRootAdmin ? (
                    <button
                      onClick={() => onTierChange(user)}
                      className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors font-medium text-sm"
                    >
                      Change Tier
                    </button>
                  ) : (
                    <div className="text-sm text-gray-400">Management restricted to Root Admins</div>
                  )}
                </div>
              )}

              {activeTab === 'history' && (
                <div className="flex gap-2 items-center">
                  {currentUser?.isRootAdmin ? (
                    <>
                      <button
                        onClick={() => onStatusToggle(user)}
                        className="px-4 py-2 rounded-lg transition-colors font-medium text-sm text-white bg-amber-600 hover:bg-amber-700"
                      >
                        {user.status === 'active' ? 'Disable' : 'Enable'}
                      </button>

                      <button
                        onClick={() => onAdminToggle(user)}
                        className="px-4 py-2 rounded-lg transition-colors font-medium text-sm text-white bg-cyan-600 hover:bg-cyan-700"
                      >
                        {user.is_admin ? 'Demote Admin' : 'Promote to Admin'}
                      </button>

                      <button
                        onClick={() => onDelete(user)}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-medium text-sm"
                      >
                        Delete User
                      </button>
                    </>
                  ) : (
                    <div className="text-sm text-gray-400">Management restricted to Root Admins</div>
                  )}
                </div>
              )}
            </div>
          )}

          <button
            onClick={onClose}
            className="px-4 py-2 text-white bg-dark-700 hover:bg-dark-600 rounded-lg transition-colors text-sm font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
